import { useEffect, useContext, useCallback } from 'react';
import { Contexts } from 'react-vtk-js';
import { useSetRecoilState, useRecoilValue, selectorFamily } from 'recoil';
import type { RecoilValueReadOnly } from 'recoil';
import { useViewportId, primitiveCursorsState, selectedPrimitiveState, useViewType } from './state';
import { useActiveSlice, useImagingContext } from './imaging/ImagingContext';

import { TOOL_CURSORS, CURSOR_DISABLED } from 'config/constants';
import { setCursor } from '../utils/dom';
import { trackPointerPosition } from '../utils/pointerPosition';
import type { ViewportTypeKeys } from 'config/constants';
import { isToolDisabled } from '../utils/isToolDisabled';

// Special ID used to identify the default cursor between the other primitive IDs
export const DEFAULT_CURSOR_ID = '__DEFAULT_CURSOR_ID__' as const;

// The tools listed here are the ones that will override any other cursor while active
export const CURSORS_WITH_PRECEDENCE = [
  'ERASER',
  'ERASE_ALL',
  'ZOOM',
  'ZOOM_IN',
  'ZOOM_OUT',
] as const;

type CursorArguments = {
  id: string;
  cursor: Nullable<string>;
};

export type SetCursor = (arg1: CursorArguments) => void;

export const useSetCursor = (): SetCursor => {
  const viewportId = useViewportId();
  const setPrimitiveCursors = useSetRecoilState(primitiveCursorsState(viewportId));

  return useCallback(
    ({ id, cursor }) => setPrimitiveCursors((state) => ({ ...state, [id]: cursor })),
    [setPrimitiveCursors]
  );
};

export const useRegisterCursor = ({ id, cursor }: CursorArguments): void => {
  const viewportId = useViewportId();
  const setPrimitiveCursors = useSetRecoilState(primitiveCursorsState(viewportId));
  useEffect(() => {
    setPrimitiveCursors((state) => ({ ...state, [id]: cursor }));
  }, [id, cursor, setPrimitiveCursors]);
};

export const withCursor = <T,>(
  Component: React.ComponentType<
    T & {
      cursor?: string;
      id: string;
    }
  >
): React.ComponentType<
  T & {
    cursor?: string;
    id: string;
  }
> =>
  function WithCursor({ id, cursor, ...props }) {
    useRegisterCursor({ id, cursor });
    // @ts-expect-error [EN-7967] - TS2322 - Type 'Omit<T & { cursor?: string; id: string; }, "cursor" | "id"> & { cursor: string; id: string; }' is not assignable to type 'IntrinsicAttributes & T & { cursor?: string; id: string; }'.
    return <Component {...props} cursor={cursor} id={id} />;
  };

export const defaultCursorSelector: (arg1: string) => RecoilValueReadOnly<Nullable<string>> =
  selectorFamily({
    key: 'viewer.dre.defaultCursorSelector',
    get:
      (viewportId: string) =>
      ({ get }) => {
        return get(primitiveCursorsState(viewportId))[DEFAULT_CURSOR_ID];
      },
    cachePolicy_UNSTABLE: {
      eviction: 'most-recent',
    },
  });
export const selectedCursorSelector: (arg1: string) => RecoilValueReadOnly<Nullable<string>> =
  selectorFamily({
    key: 'viewer.dre.selectedCursorSelector',
    get:
      (viewportId: string) =>
      ({ get }) => {
        const selectedPrimitive = get(selectedPrimitiveState(viewportId));
        return selectedPrimitive != null
          ? get(primitiveCursorsState(viewportId))[selectedPrimitive]
          : null;
      },
    cachePolicy_UNSTABLE: {
      eviction: 'most-recent',
    },
  });

/**
 * The CSS cursor this viewport should currently be showing, or `null` while the active slice's tags
 * are still resolving. Exposed so components that draw their own cursor can tell whether the cursor
 * they replace is actually the one in effect (e.g. hovering an annotation handle wins over the
 * selected tool's cursor).
 */
export const useResolvedCursor = (): Nullable<string> => {
  const viewportId = useViewportId();
  const defaultCursor = useRecoilValue(defaultCursorSelector(viewportId));
  const cursor = useRecoilValue(selectedCursorSelector(viewportId));
  const viewType = useViewType();
  const activeSlice = useActiveSlice();
  const { imagingProvider } = useImagingContext();
  const activeSliceTags = imagingProvider?.getFrameTagsForViewIndex(viewType, activeSlice);

  if (activeSliceTags?.sopClassUID == null) return null;

  return pickCursor(
    cursor,
    defaultCursor,
    viewType,
    activeSliceTags.sopClassUID,
    activeSliceTags.validSpacing
  );
};

export const useCursorHandler = () => {
  const view = useContext(Contexts.ViewContext);
  const targetCursor = useResolvedCursor();

  useEffect(() => {
    const element = view?.getViewContainer();
    if (element == null) return;
    if (targetCursor == null) return;

    setCursor(element, targetCursor);
  }, [view, targetCursor]);
};

export const CursorProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  useCursorHandler();
  // Tools that draw their own cursor need the pointer position the instant they activate, which is
  // only knowable if something has been recording it. Reference counted, so the viewports share one
  // listener. See `utils/pointerPosition.ts`.
  useEffect(trackPointerPosition, []);
  // @ts-expect-error [EN-7967] - TS2322 - Type 'ReactNode' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
  return children;
};

export const toCssCursor = (cursorConfig: { value: string; x?: number; y?: number }): string =>
  // Hotspot coordinates and an `auto` fallback are only valid after a `url(...)`; appending them to
  // a bare keyword like `none` would make the whole declaration invalid and the browser would drop
  // it, falling back to the default arrow.
  cursorConfig.value.startsWith('url(')
    ? `${cursorConfig.value} ${cursorConfig?.x ?? 0}  ${cursorConfig?.y ?? 0}, auto`
    : cursorConfig.value;

const toolCursorEntries: ReadonlyArray<[string, (typeof TOOL_CURSORS)[keyof typeof TOOL_CURSORS]]> =
  Object.entries(TOOL_CURSORS);

// FIXME(fzivolo): ideally we shouldn't store the resolved CSS cursor string in the state
const cursorReverseLookup = (cssCursor: string): Nullable<string> =>
  toolCursorEntries.find(
    ([key, config]: [any, any]) => config.value === cssCursor.split(' ')[0]
  )?.[0];

const getCursorForToolAndViewType = (
  cursor: Nullable<string>,
  viewType: ViewportTypeKeys,
  sopClassUID?: string | null,
  validSpacing?: boolean | null
) => {
  if (cursor == null) return CURSOR_DISABLED;
  const tool = cursorReverseLookup(cursor);

  if (tool != null && isToolDisabled(tool, viewType, sopClassUID, validSpacing)) {
    return CURSOR_DISABLED;
  }

  return cursor;
};

export const pickCursor = (
  cursor: Nullable<string>,
  defaultCursor: Nullable<string>,
  viewType: ViewportTypeKeys,
  sopClassUID?: string | null,
  validSpacing?: boolean | null
): Nullable<string> => {
  let targetCursor = cursor ?? defaultCursor;
  if (
    defaultCursor != null &&
    (defaultCursor.startsWith('data:image/svg+xml;base64') ||
      // @ts-expect-error [EN-7967] - TS2345 - Argument of type 'string' is not assignable to parameter of type '"ZOOM" | "ERASER" | "ERASE_ALL" | "ZOOM_OUT" | "ZOOM_IN"'.
      CURSORS_WITH_PRECEDENCE.includes(cursorReverseLookup(defaultCursor)))
  ) {
    targetCursor = defaultCursor;
  }

  targetCursor = getCursorForToolAndViewType(targetCursor, viewType, sopClassUID, validSpacing);

  return targetCursor;
};
